/**
 * Planilla: contratos, corridas y liquidaciones.
 *
 * The arithmetic lives in `payroll.ts` and was written and tested before any of this existed.
 * What this adds is the part that makes it real: who is on the payroll, what the business owes
 * each of them right now, and where every córdoba lands in the ledger.
 *
 * The rule that decides the whole design: aguinaldo and vacaciones are earned monthly and paid
 * much later. Accruing them as liabilities every run — rather than discovering them in December
 * — is what lets an October cash-flow report tell the truth about what December owes, and what
 * makes a liquidación in March a lookup instead of a reconstruction.
 *
 * Since 2026-10-12 a person is paid every week, every fifteen days or every month, a fixed amount
 * or so much a day, with or without INSS and the benefits (`planilla.ts` in shared); a run pays the
 * people of one schedule for one stretch of days, and nobody is paid twice for the same day.
 */

import {
  PAGOS_POR_ANIO,
  ZERO,
  addMoney,
  centavos,
  diasEntre,
  esRangoDe,
  finDeMes,
  mesAnterior,
  mesSiguiente,
  mesesDe,
  mulDiv,
  orderKey,
  rangoDelMes,
  seCruzan,
  subMoney,
  sumarDias,
  type AnyDomainEvent,
  type Centavos,
  type CuentaBanco,
  type FrecuenciaPago,
  type PayloadOf,
  type Rango,
} from '@abastece/shared';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import type { AccountKey } from '../ledger/accounts.js';
import { pagoDeCaja } from './caja.js';
import { cuentaCerradaParaSiempre, cuentaDeTransferencia } from './bancos.js';
import { contadaDespues, lugarEnElLog, yaLaContaron } from './contadas.js';
import {
  calcularIndemnizacion,
  calcularPlanilla,
  dias360,
  diasDeVacacionesGanados,
  inicioDelAguinaldo,
  partesDeCorrida,
  provisionesDe,
  valorDeVacaciones,
  type AcumuladoDelAnio,
  type PayrollRates,
} from '../payroll.js';
import type {
  ApplyResult,
  Effect,
  Empleado,
  LineaDeAguinaldo,
  MotivoLiquidacion,
  PagoAguinaldo,
  PagoCotizaciones,
  Planilla,
  PlanillaLinea,
  Reader,
} from '../state.js';

type FormaPagoPlanilla = 'efectivo_cordobas' | 'transferencia' | 'billetera';

const CUENTA_PAGO: Record<FormaPagoPlanilla, AccountKey> = {
  efectivo_cordobas: 'CAJA_FUERTE',
  transferencia: 'BANCOS',
  billetera: 'BILLETERA_MOVIL',
};

/** Where a payment comes out of: by transfer, the bank account it names (`bancos.ts`). */
const cuentaPago = (forma: FormaPagoPlanilla, cuenta: CuentaBanco | undefined): AccountKey =>
  forma === 'transferencia' ? cuentaDeTransferencia(cuenta) : CUENTA_PAGO[forma];

/** Only a dismissal for cause forfeits severance. Everything already earned is still paid. */
const PAGA_INDEMNIZACION: Record<MotivoLiquidacion, boolean> = {
  despido_sin_causa: true,
  renuncia: true,
  mutuo_acuerdo: true,
  despido_con_causa: false,
};

/**
 * Every way a contract can end, in the order a form should offer them.
 *
 * The array is the list, and `ETIQUETA_SALIDA` is keyed by `MotivoLiquidacion`, so adding a
 * reason to the union without giving it a label or a place in the list fails to compile.
 */
export const MOTIVOS_LIQUIDACION = [
  'renuncia',
  'despido_sin_causa',
  'despido_con_causa',
  'mutuo_acuerdo',
] as const satisfies readonly MotivoLiquidacion[];

export const ETIQUETA_SALIDA: Record<MotivoLiquidacion, string> = {
  despido_sin_causa: 'Despido sin causa justa',
  renuncia: 'Renuncia',
  mutuo_acuerdo: 'Mutuo acuerdo',
  despido_con_causa: 'Despido con causa justa',
};

/** How somebody is paid, with what a row from before any of it leaves out filled in. */
export function pagoDe(e: Pick<Empleado, 'frecuencia' | 'porDia' | 'tarifa' | 'conInss' | 'conPrestaciones'>) {
  return {
    frecuencia: e.frecuencia ?? ('mensual' as FrecuenciaPago),
    porDia: e.porDia ?? false,
    tarifa: e.tarifa ?? null,
    conInss: e.conInss ?? true,
    conPrestaciones: e.conPrestaciones ?? true,
  };
}

/**
 * A fixed pay for a run of this schedule: what is agreed for one, or the monthly salary shared out
 * over the year's runs — a monthly salary paid in a weekly run, say, after a change of schedule.
 */
export function sueldoDelPeriodo(e: Empleado, frecuencia: FrecuenciaPago): Centavos {
  const pago = pagoDe(e);
  if (pago.tarifa !== null && !pago.porDia && pago.frecuencia === frecuencia) return pago.tarifa;
  return mulDiv(e.salarioMensual, 12, PAGOS_POR_ANIO[frecuencia]);
}

/**
 * The days of a run somebody was on staff for: from the day they started, when that was during it;
 * null for the whole of it. A run that ended before they started has none.
 */
export function diasDesdeIngreso(e: Pick<Empleado, 'fechaIngreso'>, rango: Rango): number | null {
  if (rango.desde >= e.fechaIngreso) return null;
  return Math.max(0, diasEntre(e.fechaIngreso, rango.hasta) + 1);
}

/** The days a month's and a fortnight's fixed pay are for, counted as the law counts them: thirty to a month. */
const DIAS_DEL_PAGO = { mensual: 30, quincenal: 15 } as const;

/** The days a run's fixed pay is for: thirty to a month, fifteen to a fortnight, a week its own. */
export const diasDeLaCorrida = (frecuencia: FrecuenciaPago, rango: Rango): number =>
  frecuencia === 'semanal' ? diasEntre(rango.desde, rango.hasta) + 1 : DIAS_DEL_PAGO[frecuencia];

/**
 * The days of a run that pay somebody hired during it: of a month, counted as thirty (`dias360`), so
 * one day missed is one day's pay in February as in July; of a fortnight, the same days out of its
 * fifteen; of a week, its own days. Null for the whole of it.
 *
 * A fortnight used to count its calendar days, out of the calendar days of its half: hired on 24
 * October, the second half paid 8 days of 16, C$ 2,250 of C$ 4,500, while the month would have paid
 * 7 of 30, C$ 2,100, and the sheet said «a month counted as thirty»; the aguinaldo it set aside was
 * more than what a settlement pays for the same days (2026-10-31).
 */
export function diasPagados(e: Pick<Empleado, 'fechaIngreso'>, frecuencia: FrecuenciaPago, rango: Rango): number | null {
  if (rango.desde >= e.fechaIngreso) return null;
  if (frecuencia === 'semanal') return diasDesdeIngreso(e, rango);
  return Math.max(0, Math.min(DIAS_DEL_PAGO[frecuencia], dias360(e.fechaIngreso, rango.hasta)));
}

/**
 * A fixed pay for a run, for somebody hired during it: the days since they started (`diasPagados`).
 * Whole, for anybody else. A run used to pay the whole month to somebody hired on the 25th, and a
 * week that ended before they started (2026-10-13); and it counted a month's days by the calendar,
 * out of thirty, so a start on 2 February paid three days short and one on 2 January none
 * (2026-10-20). Pure, so the screen sends what the books would work out.
 */
export function sueldoDeCorrida(e: Empleado, frecuencia: FrecuenciaPago, rango: Rango): Centavos {
  const sueldo = sueldoDelPeriodo(e, frecuencia);
  const dias = diasPagados(e, frecuencia, rango);
  if (dias === null) return sueldo;
  return mulDiv(sueldo, dias, diasDeLaCorrida(frecuencia, rango));
}

/** The days a run pays: what it says, or the whole of its month, as runs before 2026-10-12 did. */
export function rangoDeCorrida(p: Pick<Planilla, 'periodo' | 'desde' | 'hasta'>): Rango {
  return p.desde && p.hasta ? { desde: p.desde, hasta: p.hasta } : rangoDelMes(p.periodo);
}

const NO_ES_PERIODO: Record<FrecuenciaPago, string> = {
  semanal: 'Una planilla semanal paga siete días',
  quincenal: 'Una planilla quincenal paga del 1 al 15 o del 16 al fin de mes',
  mensual: 'Una planilla mensual paga el mes entero',
};

export async function reducePlanilla(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'empleado.contratado':
      return contratar(event, read, rev);
    case 'empleado.actualizado':
      return actualizar(event, read, rev);
    case 'empleado.liquidado':
      return liquidar(event, read, rev);
    case 'planilla.corrida':
      return correr(event, read, rev);
    case 'planilla.pagada':
      return pagar(event, read, rev);
    case 'planilla.corregida':
      return corregir(event, read, rev);
    case 'empleado.adelanto':
      return adelantar(event, read, rev);
    case 'empleado.vacaciones':
      return descansar(event, read, rev);
    case 'cotizaciones.pagadas':
      return pagarCotizaciones(event, read, rev);
    case 'aguinaldo.pagado':
      return pagarAguinaldo(event, read, rev);
    case 'aguinaldo.corregido':
      return corregirAguinaldo(event, read, rev);
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Empleados
// ---------------------------------------------------------------------------

async function contratar(
  event: Extract<AnyDomainEvent, { type: 'empleado.contratado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  if (await read.empleado(p.empleadoId)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese empleado ya existe' }] };
  }
  if (p.usuarioId && !(await read.usuario(p.usuarioId))) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'El usuario enlazado no existe' }] };
  }

  const empleado: Empleado = {
    id: p.empleadoId,
    nombre: p.nombre,
    cedula: p.cedula,
    cargo: p.cargo,
    salarioMensual: centavos(p.salarioMensual),
    fechaIngreso: p.fechaIngreso,
    fechaSalida: null,
    inssNumero: p.inssNumero,
    telefono: p.telefono,
    usuarioId: p.usuarioId,
    formaPago: p.formaPago,
    aguinaldoAcumulado: ZERO,
    vacacionesAcumuladas: ZERO,
    diasVacacionesAcumulados: 0,
    adelantosPendientes: ZERO,
    frecuencia: p.frecuencia,
    porDia: p.porDia,
    tarifa: p.tarifa === undefined ? null : centavos(p.tarifa),
    conInss: p.conInss,
    conPrestaciones: p.conPrestaciones,
    activo: true,
    motivoSalida: null,
    rev,
  };

  // Hiring posts nothing: an employment contract is not a transaction until work is done.
  return { effects: [{ kind: 'empleado.upsert', id: p.empleadoId, row: empleado }], rejections: [] };
}

async function actualizar(
  event: Extract<AnyDomainEvent, { type: 'empleado.actualizado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const empleado = await read.empleado(p.empleadoId);

  if (!empleado) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese empleado no existe' }] };
  }
  if (!empleado.activo) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese empleado ya fue liquidado' }] };
  }

  const patch: Record<string, unknown> = {};
  for (const campo of ['nombre', 'cargo', 'cedula', 'inssNumero', 'telefono', 'usuarioId', 'formaPago'] as const) {
    if (p[campo] !== undefined) patch[campo] = p[campo] ?? null;
  }
  // A raise applies to the next run. Runs already closed are history, not a template.
  if (p.salarioMensual !== undefined) patch['salarioMensual'] = centavos(p.salarioMensual);
  for (const campo of ['frecuencia', 'porDia', 'conInss', 'conPrestaciones'] as const) {
    if (p[campo] !== undefined) patch[campo] = p[campo];
  }
  if (p.tarifa !== undefined) patch['tarifa'] = p.tarifa === null ? null : centavos(p.tarifa);

  // The day they started, set right (2026-10-31). Earlier is always taken: what runs paid stays as
  // paid. Later, not past the first day a run paid them: those days would be paid to somebody not
  // on staff yet, and their aguinaldo and vacation set aside for them.
  if (p.fechaIngreso !== undefined && p.fechaIngreso !== empleado.fechaIngreso) {
    if (p.fechaIngreso > empleado.fechaIngreso) {
      // A run that paid a day before the new one shares a day with the stretch between the two; it
      // may end in the month after.
      const meses = [...new Set([...mesesDe({ desde: empleado.fechaIngreso, hasta: p.fechaIngreso }), mesSiguiente(p.fechaIngreso.slice(0, 7))])];
      for (const c of await read.planillasDePeriodos(meses)) {
        if (c.esLiquidacion || !c.lineas.some((l) => l.empleadoId === empleado.id)) continue;
        const { desde, hasta } = rangoDeCorrida(c);
        const primero = desde > empleado.fechaIngreso ? desde : empleado.fechaIngreso;
        if (primero < p.fechaIngreso) {
          const reason = `La planilla del ${desde} al ${hasta} ya le pagó a ${empleado.nombre} desde el ${primero}: no pudo haber entrado después`;
          return { effects: [], rejections: [{ eventId: event.id, reason }] };
        }
      }
    }
    patch['fechaIngreso'] = p.fechaIngreso;
  }

  // Paid another way, said whole: by the day or not monthly, so much each time; monthly, the salary.
  const despues = pagoDe({ ...empleado, ...patch });
  const llevaTarifa = despues.porDia || despues.frecuencia !== 'mensual';
  if (llevaTarifa !== (despues.tarifa !== null)) {
    const reason = llevaTarifa ? 'Falta cuánto se le paga por día, por semana o por quincena' : 'Un sueldo fijo de cada mes es el salario mensual';
    return { effects: [], rejections: [{ eventId: event.id, reason }] };
  }

  return { effects: [{ kind: 'empleado.patch', id: p.empleadoId, rev, patch }], rejections: [] };
}

// ---------------------------------------------------------------------------
// Corrida
// ---------------------------------------------------------------------------

/** One line worked out: what it pays, and what of it comes out of what was set aside. */
export interface LineaCalculada {
  linea: PlanillaLinea;
  empleado: Empleado;
  /** Portion of the payout that draws down an existing liability, per concept. */
  contraAguinaldo: Centavos;
  contraVacaciones: Centavos;
  /** Of the salary, what paid vacation days rested on it, out of what was set aside for them. */
  descansadas: Centavos;
  /** Vacation days this run earns them, in thousandths, if it provisions vacations at all. */
  diasGanados: number;
}

function tasas(config: {
  inssLaboralBp: number;
  inssPatronalBp: number;
  inatecBp: number;
  irTramos: PayrollRates['irTramos'];
  vacacionesAcumulan?: boolean;
}): PayrollRates {
  return {
    inssLaboralBp: config.inssLaboralBp,
    inssPatronalBp: config.inssPatronalBp,
    inatecBp: config.inatecBp,
    irTramos: config.irTramos,
    ...(config.vacacionesAcumulan === false ? { vacacionesAcumulan: false } : {}),
  };
}

/**
 * Turns one requested line into a computed one.
 *
 * Splits each payout into the part that discharges an accrued liability and the part that does
 * not. Paying more aguinaldo than was ever provisioned is legitimate — somebody was hired in
 * February and the provisions started late — and the excess belongs in expense, not in a
 * negative liability that nobody can explain later.
 */
function calcularLinea(
  empleado: Empleado,
  pedido: {
    bruto: Centavos;
    otrosGravables: number;
    vacacionesPagadas: number;
    diasVacaciones: number;
    aguinaldoPagado: number;
    indemnizacion?: Centavos;
    /** Days worked, in thousandths, for somebody paid by the day. */
    dias?: number | undefined;
    /** Of a fixed pay, the days on staff in thousandths when they started during the run (`diasPagados`): what earns vacation. */
    diasEnPlanilla?: number | undefined;
    /** A settlement: the contract ends with it, and nothing accrues after it (`liquidar`). */
    esLiquidacion?: boolean;
    /** What their earlier runs of the year paid and withheld, for the income tax (`irDeCorrida`). */
    delAnio?: AcumuladoDelAnio;
    /** The part of the year it pays, in 312ths; absent, a run of its schedule. */
    partes?: number;
    /** Its own twelfth of aguinaldo is of the period the aguinaldo it pays is for: a run of November, the period's last month. */
    aguinaldoConLaDelMes?: boolean;
    /** The most of the advances it takes off: a run written down once it was paid, those given by then. */
    adelantoMaximo?: number | undefined;
  },
  rates: PayrollRates,
  /** The run's schedule: how many make a year, for the income tax and the vacation days. */
  frecuencia: FrecuenciaPago,
): LineaCalculada {
  const pago = pagoDe(empleado);
  const bruto = pedido.bruto;
  const aguinaldoPagado = centavos(pedido.aguinaldoPagado);
  const vacacionesPagadas = centavos(pedido.vacacionesPagadas);
  const indemnizacion = pedido.indemnizacion ?? ZERO;
  const pagosPorAnio = PAGOS_POR_ANIO[frecuencia];

  const r = calcularPlanilla(
    {
      bruto,
      otrosGravables: centavos(pedido.otrosGravables),
      vacacionesPagadas,
      aguinaldoPagado,
      indemnizacion,
    },
    rates,
    {
      pagosPorAnio,
      conInss: pago.conInss,
      conPrestaciones: pago.conPrestaciones,
      ...(pedido.delAnio ? { delAnio: pedido.delAnio } : {}),
      ...(pedido.partes !== undefined ? { partes: pedido.partes } : {}),
    },
  );

  // A settlement accrues nothing further: the contract ends with this payment, and what its last
  // days earned is paid in it (`liquidar`). It used to stop only when there was severance, so a
  // settlement without it — a resignation, somebody off the benefits — booked the last days'
  // accrual as owed while the person's balance went to zero: owed to nobody, for ever.
  const provisionAguinaldo = pedido.esLiquidacion ? ZERO : r.provisionAguinaldo;
  const provisionVacaciones = pedido.esLiquidacion ? ZERO : r.provisionVacaciones;
  const diasGanados = diasDeVacacionesGanados(pagosPorAnio, pago.porDia ? (pedido.dias ?? 0) : pedido.diasEnPlanilla);

  /*
   * A settlement takes what was set aside whole: the person's balances go to nothing with it, and
   * what the law pays is more or less than that (`liquidacionDe`), the gap the expense's either way.
   * A run takes what it pays, never past what is set aside; in November's, this run's own twelfth
   * too: the aguinaldo paid with November's run is the twelve of December to November, and it used
   * to take them off the eleven set aside before, book the twelfth over them as not provisioned, and
   * then set November's aside again, owed (2026-10-20). Paid in December, or late after it, the
   * run's own twelfth is the next period's.
   */
  const contraAguinaldo = pedido.esLiquidacion
    ? empleado.aguinaldoAcumulado
    : centavos(Math.min(aguinaldoPagado, empleado.aguinaldoAcumulado + (pedido.aguinaldoConLaDelMes ? provisionAguinaldo : 0)));
  const contraVacaciones = pedido.esLiquidacion
    ? empleado.vacacionesAcumuladas
    : centavos(Math.min(vacacionesPagadas, empleado.vacacionesAcumuladas));

  /*
   * Vacation days rested on the salary, with no vacation money on the line: what was set aside for
   * them pays them, so much a day as it was set aside (Arts. 76–78 CT). They used to come off the
   * days and leave their money owed, and a settlement paid it again: 15 days rested, 2.5 left, and
   * she left with C$ 7,000 «for» the 2.5 (2026-10-20).
   */
  const diasConEstos = empleado.diasVacacionesAcumulados + (provisionVacaciones > 0 ? diasGanados : 0);
  const dineroConEste = empleado.vacacionesAcumuladas + provisionVacaciones;
  const descansadas =
    !pedido.esLiquidacion && vacacionesPagadas === 0 && pedido.diasVacaciones > 0 && diasConEstos > 0 && dineroConEste > 0
      ? centavos(Math.min(bruto, mulDiv(centavos(dineroConEste), Math.min(pedido.diasVacaciones, diasConEstos), diasConEstos)))
      : ZERO;

  // What the employee owes in advances comes off what they are handed, never past it, nor past what
  // they owed the day it was handed over (`adelantoMaximo`): the rest waits for the next run. A run
  // written down after it was paid took off an advance given since, and the payment booked that
  // much less than was handed over (2026-10-31).
  const adelantoDescontado = centavos(
    Math.max(0, Math.min(empleado.adelantosPendientes ?? 0, r.neto, pedido.adelantoMaximo ?? Number.POSITIVE_INFINITY)),
  );
  const deLoApartado = addMoney(contraAguinaldo, contraVacaciones, descansadas);

  return {
    empleado,
    contraAguinaldo,
    contraVacaciones,
    descansadas,
    diasGanados,
    linea: {
      empleadoId: empleado.id,
      nombre: empleado.nombre,
      cargo: empleado.cargo,
      bruto,
      otrosGravables: centavos(pedido.otrosGravables),
      vacacionesPagadas,
      diasVacaciones: pedido.diasVacaciones,
      aguinaldoPagado,
      indemnizacion,
      gravable: r.gravable,
      exento: r.exento,
      brutoTotal: r.brutoTotal,
      inssLaboral: r.inssLaboral,
      irRetenido: r.irRetenido,
      neto: r.neto,
      inssPatronal: r.inssPatronal,
      inatec: r.inatec,
      provisionAguinaldo,
      provisionVacaciones,
      adelantoDescontado,
      ...(pedido.dias !== undefined ? { dias: pedido.dias } : {}),
      ...(deLoApartado > 0 ? { deLoApartado } : {}),
      // Its parts, and the days it earned: what a correction takes back (`revertirLinea`).
      ...(contraAguinaldo > 0 ? { contraAguinaldo } : {}),
      ...(contraVacaciones > 0 ? { contraVacaciones } : {}),
      ...(descansadas > 0 ? { descansadas } : {}),
      diasGanados: provisionVacaciones > 0 ? diasGanados : 0,
      // The days rested it took off what they had, never past it: rested past it, the balance stopped
      // at nothing, and taking the days back gave them more than they had (2026-10-31).
      ...(pedido.diasVacaciones > 0 ? { diasTomados: Math.min(pedido.diasVacaciones, Math.max(0, diasConEstos)) } : {}),
    },
  };
}

/**
 * What a line cost the business, as its entry books it: the pay, the employer's INSS and INATEC,
 * and the aguinaldo and vacation it set aside, less what of its payout earlier runs had already set
 * aside (`deLoApartado`), expensed in them. Counted again, December's run paying the aguinaldo read
 * C$ 28,820 against the C$ 16,820 its entry booked (2026-10-20).
 */
export function costoDeLinea(l: PlanillaLinea): Centavos {
  return centavos(
    l.gravable + l.exento + l.inssPatronal + l.inatec + l.provisionAguinaldo + l.provisionVacaciones - (l.deLoApartado ?? 0),
  );
}

/**
 * What a person's runs of a year already paid and withheld, for the income tax (`irDeCorrida`): a
 * run counts its part of the year, a settlement only what it paid. `anio` is `YYYY`, the year of
 * each run's `periodo`.
 */
export function acumuladoDelAnio(corridas: readonly Planilla[], empleadoId: string, anio: string): AcumuladoDelAnio {
  const a = { base: 0, ir: 0, partes: 0 };
  for (const c of corridas) {
    if (c.periodo.slice(0, 4) !== anio) continue;
    for (const l of c.lineas) {
      if (l.empleadoId !== empleadoId) continue;
      a.base += l.gravable - l.inssLaboral;
      a.ir += l.irRetenido;
      if (!c.esLiquidacion) a.partes += partesDeCorrida(PAGOS_POR_ANIO[c.frecuencia ?? 'mensual']);
    }
  }
  return a;
}

/** The months of a year, `YYYY-01` to `YYYY-12`: the runs whose income tax a run of it goes on from. */
export const mesesDelAnio = (anio: string): string[] => mesesDe({ desde: `${anio}-01-01`, hasta: `${anio}-12-31` });

/**
 * One line of a run as the books work it out once they take it (`correr`), its checks passed: what
 * was earned (as typed; the days worked at the day's pay; or the fixed pay of the run), the days of
 * it that earn vacation, the year's income tax so far, and whether its own aguinaldo twelfth goes
 * with what it pays. Pure, so «Correr planilla» says what the books will book. `corridas` holds at
 * least this year's runs.
 */
export function lineaDeCorridaEnLosLibros(
  empleado: Empleado,
  l: {
    bruto?: number | undefined;
    dias?: number | undefined;
    otrosGravables: number;
    vacacionesPagadas: number;
    diasVacaciones: number;
    aguinaldoPagado: number;
    adelantoMaximo?: number | undefined;
  },
  rates: PayrollRates,
  frecuencia: FrecuenciaPago,
  rango: Rango,
  corridas: readonly Planilla[],
): LineaCalculada {
  const pago = pagoDe(empleado);
  const bruto =
    l.bruto !== undefined
      ? centavos(l.bruto)
      : pago.porDia && pago.tarifa !== null
        ? mulDiv(pago.tarifa, l.dias ?? 0, 1000)
        : sueldoDeCorrida(empleado, frecuencia, rango);
  const pagados = diasPagados(empleado, frecuencia, rango);
  return calcularLinea(
    empleado,
    {
      ...l,
      bruto,
      ...(pagados !== null ? { diasEnPlanilla: pagados * 1000 } : {}),
      delAnio: acumuladoDelAnio(corridas, empleado.id, rango.hasta.slice(0, 4)),
      aguinaldoConLaDelMes: rango.hasta.slice(5, 7) === '11',
    },
    rates,
    frecuencia,
  );
}

/** Builds the journal entry and the employee patches shared by a run and a settlement. */
function asentar(
  calculadas: readonly LineaCalculada[],
  eventId: string,
  fecha: string,
  concepto: string,
  rev: number,
): { effects: Effect[]; totales: Omit<Planilla, 'id' | 'periodo' | 'fecha' | 'esLiquidacion' | 'lineas' | 'pagada' | 'pagadaEn' | 'nota' | 'rev'> } {
  const effects: Effect[] = [];
  const lineas: JournalLine[] = [];

  let sueldos: Centavos = ZERO;
  let aguinaldoContra: Centavos = ZERO;
  let aguinaldoGasto: Centavos = ZERO;
  let vacacionesContra: Centavos = ZERO;
  let vacacionesGasto: Centavos = ZERO;
  let descansadas: Centavos = ZERO;
  let indemnizacion: Centavos = ZERO;
  let provAguinaldo: Centavos = ZERO;
  let provVacaciones: Centavos = ZERO;

  let totalGravable: Centavos = ZERO;
  let totalExento: Centavos = ZERO;
  let totalInssLaboral: Centavos = ZERO;
  let totalIr: Centavos = ZERO;
  let totalNeto: Centavos = ZERO;
  let totalInssPatronal: Centavos = ZERO;
  let totalInatec: Centavos = ZERO;
  let totalAdelantos: Centavos = ZERO;

  for (const c of calculadas) {
    const l = c.linea;
    // The salary of vacation days rested is paid out of what was set aside for them, not spent again.
    sueldos = addMoney(sueldos, subMoney(addMoney(l.bruto, l.otrosGravables), c.descansadas));
    descansadas = addMoney(descansadas, c.descansadas);
    aguinaldoContra = addMoney(aguinaldoContra, c.contraAguinaldo);
    aguinaldoGasto = addMoney(aguinaldoGasto, subMoney(l.aguinaldoPagado, c.contraAguinaldo));
    vacacionesContra = addMoney(vacacionesContra, c.contraVacaciones);
    vacacionesGasto = addMoney(vacacionesGasto, subMoney(l.vacacionesPagadas, c.contraVacaciones));
    indemnizacion = addMoney(indemnizacion, l.indemnizacion);
    provAguinaldo = addMoney(provAguinaldo, l.provisionAguinaldo);
    provVacaciones = addMoney(provVacaciones, l.provisionVacaciones);

    totalGravable = addMoney(totalGravable, l.gravable);
    totalExento = addMoney(totalExento, l.exento);
    totalInssLaboral = addMoney(totalInssLaboral, l.inssLaboral);
    totalIr = addMoney(totalIr, l.irRetenido);
    totalNeto = addMoney(totalNeto, l.neto);
    totalInssPatronal = addMoney(totalInssPatronal, l.inssPatronal);
    totalInatec = addMoney(totalInatec, l.inatec);
    totalAdelantos = addMoney(totalAdelantos, l.adelantoDescontado ?? ZERO);

    effects.push({
      kind: 'empleado.patch',
      id: c.empleado.id,
      rev,
      patch: {
        adelantosPendientes: centavos((c.empleado.adelantosPendientes ?? 0) - (l.adelantoDescontado ?? 0)),
        aguinaldoAcumulado: centavos(
          c.empleado.aguinaldoAcumulado - c.contraAguinaldo + l.provisionAguinaldo,
        ),
        vacacionesAcumuladas: centavos(
          c.empleado.vacacionesAcumuladas - c.contraVacaciones - c.descansadas + l.provisionVacaciones,
        ),
        // 2.5 días al mes, en milésimas — o lo que gane una semana, una quincena, los días
        // trabajados —, menos lo que se pagó en este período.
        diasVacacionesAcumulados: Math.max(
          0,
          c.empleado.diasVacacionesAcumulados + (l.provisionVacaciones > 0 ? c.diasGanados : 0) - l.diasVacaciones,
        ),
      },
    });
  }

  // --- Gasto del período ---
  if (sueldos > 0) lineas.push(debit('SUELDOS', sueldos));
  if (descansadas > 0) lineas.push(debit('VACACIONES_POR_PAGAR', descansadas, 'Vacaciones descansadas'));
  if (aguinaldoContra > 0) lineas.push(debit('AGUINALDO_POR_PAGAR', aguinaldoContra, 'Aguinaldo provisionado'));
  if (aguinaldoGasto > 0) lineas.push(debit('AGUINALDO_GASTO', aguinaldoGasto, 'Aguinaldo no provisionado'));
  // A settlement pays what the law says, and what was set aside past that comes back off the expense.
  if (aguinaldoGasto < 0) lineas.push(credit('AGUINALDO_GASTO', centavos(-aguinaldoGasto), 'Provisionado de más'));
  if (vacacionesContra > 0) lineas.push(debit('VACACIONES_POR_PAGAR', vacacionesContra, 'Vacaciones provisionadas'));
  if (vacacionesGasto > 0) lineas.push(debit('VACACIONES_GASTO', vacacionesGasto));
  if (vacacionesGasto < 0) lineas.push(credit('VACACIONES_GASTO', centavos(-vacacionesGasto), 'Provisionado de más'));
  if (indemnizacion > 0) lineas.push(debit('INDEMNIZACION_GASTO', indemnizacion));
  if (totalInssPatronal > 0) lineas.push(debit('INSS_PATRONAL', totalInssPatronal));
  if (totalInatec > 0) lineas.push(debit('INATEC_GASTO', totalInatec));

  // --- Lo que queda debiendo ---
  const inssTotal = addMoney(totalInssLaboral, totalInssPatronal);
  if (inssTotal > 0) lineas.push(credit('INSS_POR_PAGAR', inssTotal, 'Laboral + patronal'));
  if (totalInatec > 0) lineas.push(credit('INATEC_POR_PAGAR', totalInatec));
  if (totalIr > 0) lineas.push(credit('IR_RETENIDO_POR_PAGAR', totalIr));
  if (totalNeto > 0) lineas.push(credit('SUELDOS_POR_PAGAR', totalNeto, 'Neto a entregar'));
  // What was already handed over in advance is not owed again.
  if (totalAdelantos > 0) {
    lineas.push(
      debit('SUELDOS_POR_PAGAR', totalAdelantos, 'Adelantos descontados'),
      credit('ADELANTOS_EMPLEADOS', totalAdelantos),
    );
  }

  // --- Provisiones del mes ---
  if (provAguinaldo > 0) {
    lineas.push(
      debit('AGUINALDO_GASTO', provAguinaldo, 'Provisión del mes'),
      credit('AGUINALDO_POR_PAGAR', provAguinaldo),
    );
  }
  if (provVacaciones > 0) {
    lineas.push(
      debit('VACACIONES_GASTO', provVacaciones, 'Provisión del mes'),
      credit('VACACIONES_POR_PAGAR', provVacaciones),
    );
  }

  // Somebody off the benefits leaving with nothing owed moves no money: no entry at all.
  if (lineas.length > 0) effects.push({ kind: 'asiento', entry: entry(eventId, fecha, concepto, lineas) });

  return {
    effects,
    totales: {
      totalGravable,
      totalExento,
      totalInssLaboral,
      totalIr,
      totalNeto,
      totalInssPatronal,
      totalInatec,
      costoTotal: addMoney(totalGravable, totalExento, totalInssPatronal, totalInatec),
      totalAdelantos,
    },
  };
}

/**
 * Why somebody's line cannot go in a run of these days, or null: they do not exist or were settled;
 * another run already paid them for a day of these (`cruzadas`); they started after it; or the days
 * they worked are missing, or more than it has. A run and a correction of one ask the same.
 */
function porQueNoVa(
  empleado: Empleado | undefined,
  l: PayloadOf<'planilla.corrida'>['lineas'][number],
  rango: Rango,
  cruzadas: readonly Planilla[],
): string | null {
  if (!empleado) return 'Un empleado de la planilla no existe';
  if (!empleado.activo) return `${empleado.nombre} ya fue liquidado y no va en la planilla`;

  const pagada = cruzadas.find((o) => o.lineas.some((x) => x.empleadoId === empleado.id));
  if (pagada) {
    const { desde, hasta } = rangoDeCorrida(pagada);
    return `${empleado.nombre} ya está en la planilla del ${desde} al ${hasta}`;
  }

  // Not on staff yet for any of it: nothing to pay; for part of it, the days since (`sueldoDeCorrida`).
  const desdeIngreso = diasDesdeIngreso(empleado, rango);
  if (desdeIngreso === 0) return `${empleado.nombre} entró el ${empleado.fechaIngreso}: no va en la planilla del ${rango.desde} al ${rango.hasta}`;

  // What they earned: as typed; or the days they worked at their day's pay; or their fixed pay
  // (`lineaDeCorridaEnLosLibros`).
  const pago = pagoDe(empleado);
  if (l.bruto === undefined && pago.porDia && pago.tarifa !== null && l.dias === undefined) return `Faltan los días que trabajó ${empleado.nombre}`;
  // Never more days than the run has, or than they were on staff: «5,5» read as 55 paid eleven
  // days' pay for a week.
  const diasPosibles = desdeIngreso ?? diasEntre(rango.desde, rango.hasta) + 1;
  if (l.dias !== undefined && l.dias > diasPosibles * 1000) {
    return `${empleado.nombre} no pudo trabajar más de ${diasPosibles} días del ${rango.desde} al ${rango.hasta}`;
  }
  return null;
}

async function correr(
  event: Extract<AnyDomainEvent, { type: 'planilla.corrida' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  if (await read.planilla(p.planillaId)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa planilla ya fue corrida' }] };
  }
  const frecuencia = p.frecuencia ?? 'mensual';
  const rango = rangoDeCorrida(p);
  if (!esRangoDe(frecuencia, rango)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: NO_ES_PERIODO[frecuencia] }] };
  }
  // The runs that share a day with this one: a week reaches back into the month before, and a run
  // of next month's may start in this one. Paying somebody twice for a day would double every
  // liability it creates.
  // And the year's runs, what each person's income tax goes on from (`irDeCorrida`).
  const leidas = await read.planillasDePeriodos([
    ...new Set([...mesesDe(rango), mesSiguiente(rango.hasta.slice(0, 7)), ...mesesDelAnio(rango.hasta.slice(0, 4))]),
  ]);
  const cruzadas = leidas.filter((o) => seCruzan(rangoDeCorrida(o), rango));

  const vistos = new Set<string>();
  const calculadas: LineaCalculada[] = [];
  const rates = tasas(await read.config());

  for (const l of p.lineas) {
    if (vistos.has(l.empleadoId)) {
      return {
        effects: [],
        rejections: [{ eventId: event.id, reason: 'Un empleado aparece dos veces en la planilla' }],
      };
    }
    vistos.add(l.empleadoId);

    const empleado = await read.empleado(l.empleadoId);
    const noVa = porQueNoVa(empleado, l, rango, cruzadas);
    if (noVa) return { effects: [], rejections: [{ eventId: event.id, reason: noVa }] };

    calculadas.push(lineaDeCorridaEnLosLibros(empleado!, l, rates, frecuencia, rango, leidas));
  }

  const { effects, totales } = asentar(
    calculadas,
    event.id,
    p.fecha,
    p.desde && p.hasta ? `Planilla del ${p.desde} al ${p.hasta}` : `Planilla ${p.periodo}`,
    rev,
  );

  const planilla: Planilla = {
    id: p.planillaId,
    periodo: p.periodo,
    fecha: p.fecha,
    frecuencia,
    // As the run says them: a run from before says none, and its month is its days.
    desde: p.desde ?? null,
    hasta: p.hasta ?? null,
    esLiquidacion: false,
    lineas: calculadas.map((c) => c.linea),
    ...totales,
    pagada: false,
    pagadaEn: null,
    nota: p.nota ?? null,
    rev,
  };

  return {
    effects: [{ kind: 'planilla.upsert', id: p.planillaId, row: planilla }, ...effects],
    rejections: [],
  };
}

// ---------------------------------------------------------------------------
// Liquidación
// ---------------------------------------------------------------------------

/**
 * Years of service between two dates, counted the way a person counts them.
 *
 * Completed anniversaries plus the fraction of the year in progress — NOT days ÷ 365.25.
 * The average-year version makes 15 Jan 2023 → 15 Jan 2026 come out as 3.0007 years because a
 * leap year happened in between, and Art. 45 prorates fractions, so that stray 0.0007 becomes a
 * few córdobas of severance nobody can explain. An employee will notice, and they will be right.
 */
export function aniosDeServicio(ingreso: string, salida: string): number {
  const desde = new Date(`${ingreso}T00:00:00Z`);
  const hasta = new Date(`${salida}T00:00:00Z`);
  if (!(hasta > desde)) return 0;

  const mes = desde.getUTCMonth();
  const dia = desde.getUTCDate();
  const aniversario = (n: number) => new Date(Date.UTC(desde.getUTCFullYear() + n, mes, dia));

  let anios = hasta.getUTCFullYear() - desde.getUTCFullYear();
  if (aniversario(anios) > hasta) anios -= 1;

  const ultimo = aniversario(anios);
  const siguiente = aniversario(anios + 1);
  const fraccion = (hasta.getTime() - ultimo.getTime()) / (siguiente.getTime() - ultimo.getTime());
  return anios + fraccion;
}

async function liquidar(
  event: Extract<AnyDomainEvent, { type: 'empleado.liquidado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const empleado = await read.empleado(p.empleadoId);

  if (!empleado) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese empleado no existe' }] };
  }
  if (!empleado.activo) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese empleado ya fue liquidado' }] };
  }
  if (p.fecha < empleado.fechaIngreso) {
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: 'La fecha de salida es anterior a la de ingreso' }],
    };
  }

  const corridas = await read.planillasDePeriodos(mesesDeLiquidacion(p.fecha));
  // Not before a day a run already paid them (2026-10-20): those days stayed paid, and their
  // aguinaldo and vacation were paid out on top. A run is refused for paying a day twice.
  const yaPagada = corridas.find(
    (c) => !c.esLiquidacion && c.lineas.some((l) => l.empleadoId === empleado.id) && pagaDespuesDe(c, p.fecha),
  );
  if (yaPagada) {
    const { desde, hasta } = rangoDeCorrida(yaPagada);
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: `La planilla del ${desde} al ${hasta} ya le pagó a ${empleado.nombre} hasta el ${hasta}: su último día no puede ser antes` }],
    };
  }

  // The amounts are the law's, not the operator's (`liquidacionDe`).
  const pago = pagoDe(empleado);
  const { calculada } = lineaDeLiquidacion(
    { empleado, fecha: p.fecha, motivo: p.motivo, salarioPendiente: centavos(p.salarioPendiente), corridas },
    tasas(await read.config()),
  );

  const { effects, totales } = asentar(
    [calculada],
    event.id,
    p.fecha,
    `Liquidación de ${empleado.nombre} — ${ETIQUETA_SALIDA[p.motivo]}`,
    rev,
  );

  const planillaId = `liq:${p.empleadoId}`;
  const planilla: Planilla = {
    id: planillaId,
    periodo: p.fecha.slice(0, 7),
    fecha: p.fecha,
    frecuencia: pago.frecuencia,
    desde: null,
    hasta: null,
    esLiquidacion: true,
    lineas: [calculada.linea],
    ...totales,
    pagada: false,
    pagadaEn: null,
    nota: p.nota ?? null,
    rev,
  };

  return {
    effects: [
      { kind: 'planilla.upsert', id: planillaId, row: planilla },
      ...effects,
      {
        kind: 'empleado.patch',
        id: p.empleadoId,
        rev,
        patch: {
          activo: false,
          fechaSalida: p.fecha,
          motivoSalida: p.motivo,
          aguinaldoAcumulado: ZERO,
          vacacionesAcumuladas: ZERO,
          diasVacacionesAcumulados: 0,
        },
      },
    ],
    rejections: [],
  };
}

/**
 * A settlement as the books book it (`liquidar`): what the law pays (`liquidacionDe`), on a line with
 * the INSS and the income tax of what of it is taxable, and the advances it takes off. Pure, so the
 * settlement's sheet says what will be handed over: it listed what the settlement is made of, with no
 * deduction and no total (2026-10-17).
 */
export function lineaDeLiquidacion(
  d: Parameters<typeof liquidacionDe>[0],
  rates: PayrollRates,
): { liquidacion: Liquidacion; calculada: LineaCalculada } {
  const pago = pagoDe(d.empleado);
  const liquidacion = liquidacionDe({ ...d, vacacionesAcumulan: rates.vacacionesAcumulan !== false });
  const delAnio = acumuladoDelAnio(d.corridas, d.empleado.id, d.fecha.slice(0, 4));
  const calculada = calcularLinea(
    d.empleado,
    {
      bruto: d.salarioPendiente,
      otrosGravables: 0,
      vacacionesPagadas: liquidacion.vacaciones,
      diasVacaciones: liquidacion.diasVacaciones,
      aguinaldoPagado: liquidacion.aguinaldo,
      indemnizacion: liquidacion.indemnizacion,
      esLiquidacion: true,
      delAnio,
      // The days it still pays are a part of the year; with none, it adds no more of the year to
      // the runs before it, unless there were none.
      partes: d.salarioPendiente > 0 || delAnio.partes === 0 ? partesDeCorrida(PAGOS_POR_ANIO[pago.frecuencia]) : 0,
    },
    rates,
    pago.frecuencia,
  );
  return { liquidacion, calculada };
}

/** Whether a run paid days after `fecha`: of a month counted as thirty, its 31st is no day more than its 30th. */
function pagaDespuesDe(c: Planilla, fecha: string): boolean {
  const { hasta } = rangoDeCorrida(c);
  if (hasta <= fecha) return false;
  return !((c.frecuencia ?? 'mensual') === 'mensual' && dias360(fecha, hasta) <= 1);
}

/**
 * The runs a settlement reads: of its year, for the income tax; of its aguinaldo period; of the six
 * months a day worker's pay is averaged over; and any that paid days past its last day.
 */
export function mesesDeLiquidacion(fecha: string): string[] {
  let desde = fecha.slice(0, 7);
  let hasta = desde;
  for (let i = 0; i < 13; i++) desde = mesAnterior(desde);
  for (let i = 0; i < 12; i++) hasta = mesSiguiente(hasta);
  return mesesDe({ desde: `${desde}-01`, hasta: `${hasta}-01` });
}

/** What a settlement pays, as the law has it (`liquidacionDe`). */
export interface Liquidacion {
  /** Years of service, the last day in. */
  anios: number;
  /** What the contract is measured by: the monthly salary; paid by the day, the six months' average (`promedioMensualDe`). */
  salario: Centavos;
  indemnizacion: Centavos;
  aguinaldo: Centavos;
  vacaciones: Centavos;
  /** The vacation days it pays, in thousandths. */
  diasVacaciones: number;
}

/**
 * What a settlement pays. The amounts are the law's, not the operator's: an operator typing these
 * under the pressure of somebody leaving is exactly where a settlement goes wrong and ends up in
 * MITRAB. Pure, so the settlement's sheet shows what the books will pay.
 *
 * - Indemnización: Art. 45 for the years served, from the first day to the last, both in. The
 *   last day used to be left out, and three years to the day paid C$ 32.80 short (2026-10-20).
 * - Aguinaldo (Arts. 93–95 CT): the days of its period that runs set it aside for, at the salary
 *   the contract ends on, a month's for 360 (`dias360`); what the salary still owed earns, a
 *   twelfth of it; and whatever of a period before is still owed, as it was set aside.
 * - Vacation (Arts. 76–78 CT): the days earned and not rested, at a thirtieth of that salary each,
 *   and what the salary still owed earns, a twelfth of it.
 *
 * Both used to be what each run set aside at the salary of its day: after a raise in June, seven
 * months paid C$ 14,500 against the law's C$ 17,500 (2026-10-20). Days no run of the app paid —
 * before the shop kept its payroll here — are not counted, as they never were. Somebody paid by
 * the day has no monthly salary to measure them by: what they earned a month, on average, over the
 * last six. Off the benefits (2026-10-12), no severance, and what accrued before they were taken
 * off is paid.
 */
export function liquidacionDe(d: {
  empleado: Empleado;
  fecha: string;
  motivo: MotivoLiquidacion;
  salarioPendiente: Centavos;
  /** The runs of `mesesDeLiquidacion`, or more. */
  corridas: readonly Planilla[];
  /** The shop's vacations accrue (`vacacionesAcumulan`): false, the salary still owed earns none. */
  vacacionesAcumulan?: boolean;
}): Liquidacion {
  const e = d.empleado;
  const pago = pagoDe(e);
  const salario = pago.porDia ? promedioMensualDe(d.corridas, e, d.fecha) : e.salarioMensual;
  const anios = aniosDeServicio(e.fechaIngreso, sumarDias(d.fecha, 1));
  const indemnizacion = PAGA_INDEMNIZACION[d.motivo] && pago.conPrestaciones ? calcularIndemnizacion(salario, anios) : ZERO;
  if (!pago.conPrestaciones) {
    return {
      anios,
      salario,
      indemnizacion,
      aguinaldo: e.aguinaldoAcumulado,
      vacaciones: e.vacacionesAcumuladas,
      diasVacaciones: e.diasVacacionesAcumulados,
    };
  }

  // The runs of its period that set its aguinaldo aside, and the days of each they paid.
  const delPeriodo = new Set(mesesDe({ desde: inicioDelAguinaldo(d.fecha), hasta: d.fecha }));
  let dias = 0;
  let apartadoDelPeriodo = 0;
  for (const c of d.corridas) {
    if (c.esLiquidacion || !delPeriodo.has(c.periodo)) continue;
    const l = c.lineas.find((x) => x.empleadoId === e.id);
    if (!l || l.provisionAguinaldo <= 0) continue;
    const r = rangoDeCorrida(c);
    const desde = r.desde > e.fechaIngreso ? r.desde : e.fechaIngreso;
    const hasta = r.hasta < d.fecha ? r.hasta : d.fecha;
    if (desde <= hasta) dias += dias360(desde, hasta);
    apartadoDelPeriodo += l.provisionAguinaldo;
  }
  const deAntes = centavos(Math.max(0, e.aguinaldoAcumulado - apartadoDelPeriodo));

  // Off for the shop, what was set aside before is still paid, and the salary still owed earns no more.
  const conVacaciones = d.vacacionesAcumulan !== false;
  const ultimos = devengoDeLiquidacion(d.salarioPendiente, true, conVacaciones);
  // The days the salary still owed earns: two and a half for a month of it.
  const diasUltimos = conVacaciones && salario > 0 ? Math.round((d.salarioPendiente * 2_500) / salario) : 0;
  return {
    anios,
    salario,
    indemnizacion,
    aguinaldo: addMoney(mulDiv(salario, Math.min(360, dias), 360), ultimos.aguinaldo, deAntes),
    vacaciones: addMoney(valorDeVacaciones(salario, e.diasVacacionesAcumulados), ultimos.vacaciones),
    diasVacaciones: e.diasVacacionesAcumulados + diasUltimos,
  };
}

/**
 * What the salary still owed at a settlement earns toward the aguinaldo and the vacation: a twelfth
 * of it each, as every run accrues (`calcularPlanilla`), and nothing off the benefits.
 */
export function devengoDeLiquidacion(
  salarioPendiente: Centavos,
  conPrestaciones: boolean,
  conVacaciones = true,
): { aguinaldo: Centavos; vacaciones: Centavos } {
  const p = provisionesDe(salarioPendiente, conPrestaciones, conVacaciones);
  return { aguinaldo: p.provisionAguinaldo, vacaciones: p.provisionVacaciones };
}

/** The six months up to the one of `fecha`, that one first: what a day worker's severance averages over. */
export function seisMesesHasta(fecha: string): string[] {
  const periodos = [fecha.slice(0, 7)];
  while (periodos.length < 6) periodos.push(mesAnterior(periodos[periodos.length - 1]!));
  return periodos;
}

/**
 * What somebody paid by the day earned a month, on average, in the runs of the six months up to
 * when they leave: or over the months since they started, if fewer. Pure, so the screen shows the
 * severance the books will pay.
 *
 * The month they leave counts when a run paid it. Otherwise its days are paid with the settlement
 * (`salarioPendiente`), and it is the six months before it: counted as a month of nothing, it took a
 * sixth off the average (2026-10-13). With no run at all in those months — paid outside the app,
 * say — what was agreed a month (`salarioMensual`), not nothing.
 */
export function promedioMensualDe(
  corridas: readonly Planilla[],
  e: Pick<Empleado, 'id' | 'fechaIngreso' | 'salarioMensual'>,
  fecha: string,
): Centavos {
  const suyas = corridas.filter((c) => !c.esLiquidacion && c.lineas.some((l) => l.empleadoId === e.id));
  const mesDeSalida = fecha.slice(0, 7);
  const hasta = suyas.some((c) => c.periodo === mesDeSalida) ? fecha : finDeMes(mesAnterior(mesDeSalida));
  const periodos = seisMesesHasta(hasta);
  const total = suyas
    .filter((c) => periodos.includes(c.periodo))
    .flatMap((c) => c.lineas)
    .filter((l) => l.empleadoId === e.id)
    .reduce((s, l) => s + l.bruto, 0);
  if (total === 0) return e.salarioMensual;
  const meses = Math.min(6, Math.max(1, aniosDeServicio(e.fechaIngreso, hasta) * 12));
  return centavos(Math.round(total / meses));
}

// ---------------------------------------------------------------------------
// Pagos
// ---------------------------------------------------------------------------

async function pagar(
  event: Extract<AnyDomainEvent, { type: 'planilla.pagada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const planilla = await read.planilla(p.planillaId);

  if (!planilla) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa planilla no existe' }] };
  }
  if (planilla.pagada) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa planilla ya fue pagada' }] };
  }
  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
  if (planilla.totalNeto < 0) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa planilla no tiene nada que pagar' }] };
  }

  // Advances already covered part of it, or all of it — or it came to nothing, a settlement with
  // nothing owed, which used to wait to be paid for ever: then only the record says it is settled.
  const aEntregar = subMoney(planilla.totalNeto, planilla.totalAdelantos ?? ZERO);
  return {
    effects: [
      { kind: 'planilla.patch', id: p.planillaId, rev, patch: { pagada: true, pagadaEn: p.fecha } },
      ...(aEntregar > 0
        ? [
            {
              kind: 'asiento' as const,
              entry: entry(event.id, p.fecha, `Pago de planilla ${planilla.periodo}`, [
                debit('SUELDOS_POR_PAGAR', aEntregar),
                credit(cuentaPago(p.formaPago, p.cuenta), aEntregar, p.referencia),
              ]),
            },
          ]
        : []),
    ],
    rejections: [],
  };
}

// ---------------------------------------------------------------------------
// Corrección
// ---------------------------------------------------------------------------

/**
 * What a line took out of what earlier runs had set aside, in its parts, and the vacation days it
 * earned (`asentar`): kept on the line since 2026-10-31. A line from before has them worked out
 * again: the aguinaldo first, then the vacation paid, the rest days rested; the days as a run of its
 * days earns them.
 */
export function partesDeLaLinea(
  l: PlanillaLinea,
  e: Empleado,
  frecuencia: FrecuenciaPago,
  rango: Rango,
): { contraAguinaldo: Centavos; contraVacaciones: Centavos; descansadas: Centavos; diasGanados: number; diasTomados: number } {
  if (l.diasGanados !== undefined) {
    return {
      contraAguinaldo: l.contraAguinaldo ?? ZERO,
      contraVacaciones: l.contraVacaciones ?? ZERO,
      descansadas: l.descansadas ?? ZERO,
      diasGanados: l.diasGanados,
      diasTomados: l.diasTomados ?? 0,
    };
  }
  const apartado = l.deLoApartado ?? ZERO;
  const contraAguinaldo = centavos(Math.min(l.aguinaldoPagado, apartado));
  const contraVacaciones = centavos(Math.min(l.vacacionesPagadas, apartado - contraAguinaldo));
  const pagados = diasPagados(e, frecuencia, rango);
  return {
    contraAguinaldo,
    contraVacaciones,
    descansadas: centavos(apartado - contraAguinaldo - contraVacaciones),
    diasGanados:
      l.provisionVacaciones > 0
        ? diasDeVacacionesGanados(PAGOS_POR_ANIO[frecuencia], pagoDe(e).porDia ? (l.dias ?? 0) : pagados === null ? undefined : pagados * 1000)
        : 0,
    diasTomados: l.diasVacaciones,
  };
}

/** Somebody as they were before a run's line was booked: what it moved on them, taken back (`asentar`). */
export function revertirLinea(e: Empleado, l: PlanillaLinea, frecuencia: FrecuenciaPago, rango: Rango): Empleado {
  const p = partesDeLaLinea(l, e, frecuencia, rango);
  return {
    ...e,
    adelantosPendientes: centavos((e.adelantosPendientes ?? 0) + (l.adelantoDescontado ?? 0)),
    aguinaldoAcumulado: centavos(e.aguinaldoAcumulado + p.contraAguinaldo - l.provisionAguinaldo),
    vacacionesAcumuladas: centavos(e.vacacionesAcumuladas + p.contraVacaciones + p.descansadas - l.provisionVacaciones),
    diasVacacionesAcumulados: Math.max(0, e.diasVacacionesAcumulados - p.diasGanados + p.diasTomados),
  };
}

/** The lines of the entry `asentar` books, or none. */
const lineasDelAsiento = (effects: readonly Effect[]): JournalLine[] => effects.flatMap((e) => (e.kind === 'asiento' ? e.entry.lineas : []));

/**
 * A run set right (`planilla.corregida`, the owner, 2026-10-31: «we need a way to be able to edit
 * payroll runs from the runs tab for corrections»): the same days, its lines as they should have
 * been, or none to void it.
 *
 * Everybody it had is taken back to before it (`revertirLinea`), and the new lines are worked out from
 * there as a run would, with the same checks (`porQueNoVa`) and the income tax going on from the year's
 * runs before it. The books get one entry, on the run's own day: what the new lines book less what the
 * old ones did. Runs after it keep what they paid and withheld; what they set aside goes on from the
 * difference, and a correction that would leave somebody's aguinaldo or vacation set aside below
 * nothing, because a later run already paid it out, is refused.
 *
 * Paid, the payment follows (a correction fixes the record of what was handed over): the difference
 * goes out of, or back into, the account that paid it, on the day it was paid (`pagoId`). Refused when
 * that money was counted since, as a voided payment is: the count already found what it really was.
 */
async function corregir(
  event: Extract<AnyDomainEvent, { type: 'planilla.corregida' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const planilla = await read.planilla(p.planillaId);
  if (!planilla) return rechazo('Esa planilla no existe');
  if (planilla.esLiquidacion) return rechazo('Una liquidación no se corrige');

  const frecuencia = planilla.frecuencia ?? 'mensual';
  const rango = rangoDeCorrida(planilla);
  const leidas = (
    await read.planillasDePeriodos([
      ...new Set([...mesesDe(rango), mesSiguiente(rango.hasta.slice(0, 7)), ...mesesDelAnio(rango.hasta.slice(0, 4))]),
    ])
  ).filter((o) => o.id !== planilla.id);
  const cruzadas = leidas.filter((o) => seCruzan(rangoDeCorrida(o), rango));
  // What their income tax goes on from: the year's runs before this one.
  const antes = leidas.filter((o) => (o.esLiquidacion ? o.fecha : rangoDeCorrida(o).hasta) < rango.desde);
  const config = await read.config();
  const rates = tasas(config);

  // Everybody it had, as they were before it.
  const comoEstaban = new Map<string, Empleado>();
  const viejas: LineaCalculada[] = [];
  for (const l of planilla.lineas) {
    const e = await read.empleado(l.empleadoId);
    if (!e) return rechazo('Un empleado de la planilla no existe');
    if (!e.activo) return rechazo(`${e.nombre} ya fue liquidado: la planilla del ${rango.desde} al ${rango.hasta} ya no se corrige`);
    comoEstaban.set(e.id, revertirLinea(e, l, frecuencia, rango));
    viejas.push({ empleado: e, linea: l, ...partesDeLaLinea(l, e, frecuencia, rango) });
  }

  // Each line as it should have been, from them as they were. What a later run paid out of what this
  // one set aside leaves them below nothing before it: a line pays out of no more than nothing, and
  // what they end with is still that, plus what the line sets aside, less what it pays.
  const vistos = new Set<string>();
  const nuevas: LineaCalculada[] = [];
  for (const l of p.lineas) {
    if (vistos.has(l.empleadoId)) return rechazo('Un empleado aparece dos veces en la planilla');
    vistos.add(l.empleadoId);
    const estaba = comoEstaban.get(l.empleadoId);
    const empleado = estaba
      ? { ...estaba, aguinaldoAcumulado: centavos(Math.max(0, estaba.aguinaldoAcumulado)), vacacionesAcumuladas: centavos(Math.max(0, estaba.vacacionesAcumuladas)) }
      : await read.empleado(l.empleadoId);
    const noVa = porQueNoVa(empleado, l, rango, cruzadas);
    if (noVa) return rechazo(noVa);
    nuevas.push(lineaDeCorridaEnLosLibros(empleado!, l, rates, frecuencia, rango, antes));
  }

  const concepto = planilla.desde && planilla.hasta ? `del ${planilla.desde} al ${planilla.hasta}` : planilla.periodo;
  const nuevo = asentar(nuevas, event.id, planilla.fecha, '', rev);
  // Taken out of the run, they are as they were before it.
  const quitados: Effect[] = viejas
    .filter((c) => !vistos.has(c.empleado.id))
    .map((c) => {
      const e = comoEstaban.get(c.empleado.id)!;
      return {
        kind: 'empleado.patch',
        id: e.id,
        rev,
        patch: {
          adelantosPendientes: e.adelantosPendientes,
          aguinaldoAcumulado: e.aguinaldoAcumulado,
          vacacionesAcumuladas: e.vacacionesAcumuladas,
          diasVacacionesAcumulados: e.diasVacacionesAcumulados,
        },
      };
    });
  const patches = [
    ...nuevo.effects.flatMap((e): Effect[] => {
      if (e.kind !== 'empleado.patch') return [];
      const estaba = comoEstaban.get(e.id);
      if (!estaba) return [e];
      const debajo = (x: number) => Math.min(0, x);
      return [
        {
          ...e,
          patch: {
            ...e.patch,
            aguinaldoAcumulado: centavos((e.patch.aguinaldoAcumulado ?? 0) + debajo(estaba.aguinaldoAcumulado)),
            vacacionesAcumuladas: centavos((e.patch.vacacionesAcumuladas ?? 0) + debajo(estaba.vacacionesAcumuladas)),
          },
        },
      ];
    }),
    ...quitados,
  ];
  for (const e of patches) {
    if (e.kind !== 'empleado.patch') continue;
    const nombre = comoEstaban.get(e.id)?.nombre ?? nuevas.find((c) => c.empleado.id === e.id)?.empleado.nombre ?? '';
    if ((e.patch.aguinaldoAcumulado ?? 0) < 0) return rechazo(`Así, el aguinaldo apartado de ${nombre} quedaría por debajo de cero: una planilla de después ya pagó lo que esta apartaba`);
    if ((e.patch.vacacionesAcumuladas ?? 0) < 0) return rechazo(`Así, las vacaciones apartadas de ${nombre} quedarían por debajo de cero: una planilla de después ya pagó lo que esta apartaba`);
  }

  // One entry on the run's day: what it books now, less what it booked.
  const neto = new Map<string, number>();
  for (const [lineas, signo] of [
    [lineasDelAsiento(nuevo.effects), 1],
    [lineasDelAsiento(asentar(viejas, event.id, planilla.fecha, '', rev).effects), -1],
  ] as const) {
    for (const l of lineas) neto.set(l.cuenta, (neto.get(l.cuenta) ?? 0) + signo * (l.debe - l.haber));
  }
  const diferencia: JournalLine[] = [...neto]
    .filter(([, monto]) => monto !== 0)
    .map(([cuenta, monto]) => (monto > 0 ? { cuenta, debe: centavos(monto), haber: ZERO } : { cuenta, debe: ZERO, haber: centavos(-monto) }));

  // Paid, the payment follows: the difference out of, or back into, what paid it, on its day.
  const antesEntregar = subMoney(planilla.totalNeto, planilla.totalAdelantos ?? ZERO);
  const ahoraEntregar = subMoney(nuevo.totales.totalNeto, nuevo.totales.totalAdelantos ?? ZERO);
  const delPago = ahoraEntregar - antesEntregar;
  const pagoCorregido: Effect[] = [];
  if (planilla.pagada && delPago !== 0) {
    const pago = p.pagoId ? await read.eventoDelLog(p.pagoId) : null;
    const dice = pago?.tipo === 'planilla.pagada' ? (pago.payload as PayloadOf<'planilla.pagada'>) : null;
    if (!pago || !dice || dice.planillaId !== planilla.id || dice.fecha !== planilla.pagadaEn) {
      return rechazo('Esa planilla ya se pagó, y falta su pago: el pago cambia con la corrección');
    }
    const cerrada = cuentaCerradaParaSiempre(config, dice.cuenta);
    if (cerrada) return rechazo(cerrada);
    const cuenta = cuentaPago(dice.formaPago, dice.cuenta);
    if (contadaDespues(config, cuenta, pago.lugar, dice.fecha)) return rechazo(yaLaContaron(cuenta, 'ese pago', config));
    const monto = centavos(Math.abs(delPago));
    pagoCorregido.push({
      kind: 'asiento',
      entry: entry(
        event.id,
        dice.fecha,
        `Pago de planilla ${planilla.periodo}, corregido`,
        delPago > 0 ? [debit('SUELDOS_POR_PAGAR', monto), credit(cuenta, monto, dice.referencia)] : [debit(cuenta, monto, dice.referencia), credit('SUELDOS_POR_PAGAR', monto)],
      ),
    });
  }

  // A patch, not an upsert: the server inserts a run once and leaves an existing row alone.
  const cambios: Partial<Omit<Planilla, 'id'>> = { lineas: nuevas.map((c) => c.linea), ...nuevo.totales, nota: p.nota ?? planilla.nota };
  return {
    effects: [
      { kind: 'planilla.patch', id: planilla.id, rev, patch: cambios },
      ...patches,
      ...(diferencia.length > 0
        ? [{ kind: 'asiento' as const, entry: entry(event.id, planilla.fecha, `Corrección de la planilla ${concepto}`, diferencia) }]
        : []),
      ...pagoCorregido,
    ],
    rejections: [],
  };
}

/**
 * A salary advance: the employee owes it until a run takes it off their net (`asentar`). Out of
 * general cash, the bank or the wallet — or out of the drawer of an open till, which then stops
 * expecting it, the way a purchase or an expense paid from the drawer does.
 */
async function adelantar(
  event: Extract<AnyDomainEvent, { type: 'empleado.adelanto' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const empleado = await read.empleado(p.empleadoId);
  if (!empleado) return rechazo('Ese empleado no existe');
  if (!empleado.activo) return rechazo('Ese empleado ya fue liquidado');
  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return rechazo(cerrada);

  const monto = centavos(p.monto);
  const deCaja = p.sesionId ? await pagoDeCaja(read, p.sesionId, p.formaPago, monto, rev) : null;
  if (deCaja && 'rechazo' in deCaja) return rechazo(deCaja.rechazo);

  return {
    effects: [
      ...(deCaja ? [deCaja.efecto] : []),
      {
        kind: 'empleado.patch',
        id: empleado.id,
        rev,
        patch: { adelantosPendientes: addMoney(empleado.adelantosPendientes ?? ZERO, monto) },
      },
      {
        kind: 'asiento',
        entry: entry(event.id, p.fecha, `Adelanto a ${empleado.nombre}${p.nota ? `: ${p.nota}` : ''}`, [
          debit('ADELANTOS_EMPLEADOS', monto),
          credit(deCaja ? 'CAJA_CORDOBAS' : cuentaPago(p.formaPago, p.cuenta), monto, deCaja ? 'Pagado de la gaveta' : undefined),
        ]),
      },
    ],
    rejections: [],
  };
}

/**
 * What vacation days rested come out of what was set aside for the vacation: so much a day as it was
 * set aside, never past the days earned. Pure, so the sheet says what the books will do.
 */
export function vacacionesDescansadas(
  e: Pick<Empleado, 'vacacionesAcumuladas' | 'diasVacacionesAcumulados'>,
  dias: number,
): { valor: Centavos; quedan: number; sinGanar: number } {
  const acumulados = e.diasVacacionesAcumulados;
  const valor =
    acumulados > 0 && e.vacacionesAcumuladas > 0 ? mulDiv(e.vacacionesAcumuladas, Math.min(dias, acumulados), acumulados) : ZERO;
  return { valor, quedan: Math.max(0, acumulados - dias), sinGanar: Math.max(0, dias - acumulados) };
}

/**
 * Vacation days rested, written down outside a run (`empleado.vacaciones`, 2026-10-31). The run of
 * those days pays their salary as always; what was set aside pays it instead of the salary expense,
 * as with the days typed on a run's line (`descansadas`): the days come off what was earned, and the
 * money off what was set aside. Somebody paid by the day is paid no salary on the days they rest:
 * their vacation is paid in a run, as money.
 */
async function descansar(
  event: Extract<AnyDomainEvent, { type: 'empleado.vacaciones' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const empleado = await read.empleado(p.empleadoId);
  if (!empleado) return rechazo('Ese empleado no existe');
  if (!empleado.activo) return rechazo('Ese empleado ya fue liquidado');
  if (p.desde < empleado.fechaIngreso) return rechazo(`${empleado.nombre} entró el ${empleado.fechaIngreso}: no descansó antes`);
  const posibles = diasEntre(p.desde, p.hasta) + 1;
  if (p.dias > posibles * 1000) return rechazo(`Del ${p.desde} al ${p.hasta} no hay más de ${posibles} días`);
  if (pagoDe(empleado).porDia) {
    return rechazo(`A ${empleado.nombre} se le paga por día: sus vacaciones se le pagan en la planilla, en «Vacaciones pagadas»`);
  }

  const { valor, quedan } = vacacionesDescansadas(empleado, p.dias);
  return {
    effects: [
      {
        kind: 'empleado.patch',
        id: empleado.id,
        rev,
        patch: { vacacionesAcumuladas: subMoney(empleado.vacacionesAcumuladas, valor), diasVacacionesAcumulados: quedan },
      },
      ...(valor > 0
        ? [
            {
              kind: 'asiento' as const,
              entry: entry(event.id, p.desde, `Vacaciones de ${empleado.nombre} del ${p.desde} al ${p.hasta}`, [
                debit('VACACIONES_POR_PAGAR', valor, 'Vacaciones descansadas'),
                credit('SUELDOS', valor, 'Su salario sale de lo apartado'),
              ]),
            },
          ]
        : []),
    ],
    rejections: [],
  };
}

async function pagarCotizaciones(
  event: Extract<AnyDomainEvent, { type: 'cotizaciones.pagadas' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  if (await read.pagoCotizaciones(p.pagoId)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pago ya fue registrado' }] };
  }
  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };

  const inss = centavos(p.inss);
  const inatec = centavos(p.inatec);
  const ir = centavos(p.ir);
  const total = addMoney(inss, inatec, ir);

  if (total <= 0) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'El pago no lleva ningún monto' }] };
  }

  const row: PagoCotizaciones = {
    id: p.pagoId,
    fecha: p.fecha,
    periodo: p.periodo,
    inss,
    inatec,
    ir,
    total,
    cuenta: cuentaPago(p.formaPago, p.cuenta),
    referencia: p.referencia ?? null,
    rev,
  };

  return {
    effects: [
      { kind: 'pagoCotizaciones.upsert', id: p.pagoId, row },
      {
        kind: 'asiento',
        entry: entry(event.id, p.fecha, `Cotizaciones ${p.periodo}`, [
          debit('INSS_POR_PAGAR', inss),
          debit('INATEC_POR_PAGAR', inatec),
          debit('IR_RETENIDO_POR_PAGAR', ir),
          credit(cuentaPago(p.formaPago, p.cuenta), total, p.referencia),
        ]),
      },
    ],
    rejections: [],
  };
}

// ---------------------------------------------------------------------------
// Aguinaldo, pagado aparte
// ---------------------------------------------------------------------------

/**
 * The aguinaldo paid on its own (`aguinaldo.pagado`, the owner, 2026-10-31: «let's separate the pay
 * aguinaldo function as a button on the payroll page and take that shit out of the normal payroll
 * form»). Each person on it is handed what it says, with no INSS nor income tax (Art. 95 CT): out of
 * what the runs set aside for them, and what goes past that is an expense of the payment, as a run's
 * aguinaldo past it was. One entry on the day it was paid, out of what paid it. Somebody settled is
 * refused: the settlement paid what was set aside.
 */
async function pagarAguinaldo(
  event: Extract<AnyDomainEvent, { type: 'aguinaldo.pagado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  if (await read.pagoAguinaldo(p.pagoId)) return rechazo('Ese pago ya fue registrado');
  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return rechazo(cerrada);

  const quienes: Empleado[] = [];
  const lineas: LineaDeAguinaldo[] = [];
  for (const l of p.lineas) {
    if (quienes.some((e) => e.id === l.empleadoId)) return rechazo('Un empleado aparece dos veces en el pago');
    const e = await read.empleado(l.empleadoId);
    if (!e) return rechazo('Un empleado del pago no existe');
    if (!e.activo) return rechazo(`${e.nombre} ya fue liquidado: su aguinaldo se pagó en la liquidación`);
    quienes.push(e);
    lineas.push(lineaDeAguinaldo(e, l.monto, centavos(Math.max(0, e.aguinaldoAcumulado))));
  }

  const cuenta = cuentaPago(p.formaPago, p.cuenta);
  const row: PagoAguinaldo = {
    id: p.pagoId,
    fecha: p.fecha,
    lineas,
    total: centavos(lineas.reduce((t, l) => t + l.monto, 0)),
    formaPago: p.formaPago,
    cuenta,
    referencia: p.referencia ?? null,
    nota: null,
    lugar: lugarEnElLog(event),
    rev,
  };
  return {
    effects: [
      { kind: 'pagoAguinaldo.upsert', id: p.pagoId, row },
      ...quienes.map(
        (e, k): Effect => ({ kind: 'empleado.patch', id: e.id, rev, patch: { aguinaldoAcumulado: centavos(e.aguinaldoAcumulado - lineas[k]!.deLoApartado) } }),
      ),
      { kind: 'asiento', entry: entry(event.id, p.fecha, 'Aguinaldo', lineasDelAguinaldo(lineas, 1, cuenta, p.referencia)) },
    ],
    rejections: [],
  };
}

/** Somebody's line of an aguinaldo payment: of `monto`, what comes out of `apartado`. */
const lineaDeAguinaldo = (e: Pick<Empleado, 'id' | 'nombre'>, monto: number, apartado: Centavos): LineaDeAguinaldo => ({
  empleadoId: e.id,
  nombre: e.nombre,
  monto: centavos(monto),
  apartado,
  deLoApartado: centavos(Math.min(monto, apartado)),
});

/**
 * What aguinaldo lines book, `signo` 1, or take back, -1: off what is owed for it, what came out of
 * it; past that, an expense; and the money, out of what paid it. None for what comes to nothing.
 */
function lineasDelAguinaldo(lineas: readonly LineaDeAguinaldo[], signo: 1 | -1, cuenta: AccountKey, referencia?: string): JournalLine[] {
  const deLoApartado = signo * lineas.reduce((t, l) => t + l.deLoApartado, 0);
  const gasto = signo * lineas.reduce((t, l) => t + l.monto - l.deLoApartado, 0);
  const total = deLoApartado + gasto;
  const lado = (cuentaDe: AccountKey, monto: number, memo?: string): JournalLine[] =>
    monto === 0 ? [] : [monto > 0 ? debit(cuentaDe, centavos(monto), memo) : credit(cuentaDe, centavos(-monto), memo)];
  return [
    ...lado('AGUINALDO_POR_PAGAR', deLoApartado),
    ...lado('AGUINALDO_GASTO', gasto, 'Más de lo apartado'),
    ...lado(cuenta, -total, referencia),
  ];
}

/**
 * An aguinaldo payment set right (`aguinaldo.corregido`, 2026-10-31), as a run is: its lines as they
 * should have been, or none to void it. Each person on it takes back what it took out of what was set
 * aside for them, and the new line takes out of what was set aside when it was paid; somebody added,
 * out of what is set aside now. One entry on its day, the difference, the money out of or back into
 * what paid it: refused once that money was counted since, as a voided payment is, and when what is
 * set aside for somebody would end below nothing because it was paid out since. Somebody on it
 * settled since: refused.
 */
async function corregirAguinaldo(
  event: Extract<AnyDomainEvent, { type: 'aguinaldo.corregido' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const pago = await read.pagoAguinaldo(p.pagoId);
  if (!pago) return rechazo('Ese pago de aguinaldo no existe');

  const gente = new Map<string, Empleado>();
  for (const id of [...pago.lineas.map((l) => l.empleadoId), ...p.lineas.map((l) => l.empleadoId)]) {
    if (gente.has(id)) continue;
    const e = await read.empleado(id);
    if (!e) return rechazo('Un empleado del pago no existe');
    if (!e.activo) return rechazo(`${e.nombre} ya fue liquidado: el aguinaldo pagado el ${pago.fecha} ya no se corrige`);
    gente.set(id, e);
  }
  const lineas: LineaDeAguinaldo[] = [];
  for (const l of p.lineas) {
    if (lineas.some((x) => x.empleadoId === l.empleadoId)) return rechazo('Un empleado aparece dos veces en el pago');
    const e = gente.get(l.empleadoId)!;
    const antes = pago.lineas.find((x) => x.empleadoId === l.empleadoId);
    lineas.push(lineaDeAguinaldo(e, l.monto, antes ? antes.apartado : centavos(Math.max(0, e.aguinaldoAcumulado))));
  }

  const config = await read.config();
  const cuenta = pago.cuenta as AccountKey;
  const total = centavos(lineas.reduce((t, l) => t + l.monto, 0));
  if (total !== pago.total && contadaDespues(config, cuenta, pago.lugar, pago.fecha)) return rechazo(yaLaContaron(cuenta, 'ese pago', config));

  const patches: Effect[] = [];
  for (const e of gente.values()) {
    const tomo = (xs: readonly LineaDeAguinaldo[]) => xs.find((x) => x.empleadoId === e.id)?.deLoApartado ?? 0;
    const queda = e.aguinaldoAcumulado + tomo(pago.lineas) - tomo(lineas);
    if (queda < 0) return rechazo(`Así, el aguinaldo apartado de ${e.nombre} quedaría por debajo de cero: ya se pagó después`);
    if (queda !== e.aguinaldoAcumulado) patches.push({ kind: 'empleado.patch', id: e.id, rev, patch: { aguinaldoAcumulado: centavos(queda) } });
  }

  // One entry on its day: what the new lines book, less what the old ones did.
  const neto = new Map<string, number>();
  for (const l of [...lineasDelAguinaldo(lineas, 1, cuenta), ...lineasDelAguinaldo(pago.lineas, -1, cuenta)]) {
    neto.set(l.cuenta, (neto.get(l.cuenta) ?? 0) + l.debe - l.haber);
  }
  const diferencia: JournalLine[] = [...neto]
    .filter(([, monto]) => monto !== 0)
    .map(([c, monto]) => (monto > 0 ? { cuenta: c, debe: centavos(monto), haber: ZERO } : { cuenta: c, debe: ZERO, haber: centavos(-monto) }));

  return {
    effects: [
      // A patch, not an upsert: the server writes a row once and leaves it alone after.
      { kind: 'pagoAguinaldo.patch', id: pago.id, rev, patch: { lineas, total, nota: p.nota ?? pago.nota } },
      ...patches,
      ...(diferencia.length > 0 ? [{ kind: 'asiento' as const, entry: entry(event.id, pago.fecha, 'Aguinaldo, corregido', diferencia) }] : []),
    ],
    rejections: [],
  };
}

